/* Clear inline picture; controls live beneath the video, never over it. */
(() => {
  if (customElements.get('cp-film-player')) return;
  const time = value => {
    const seconds = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  };
  class FilmPlayer extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.video = this.querySelector('video');
      this.controls = this.querySelector('[data-film-controls]');
      if (!this.video || !this.controls) return;
      this.poster = this.querySelector('[data-film-poster]');
      const posterUrl = this.video.getAttribute('poster');
      if (!this.poster && posterUrl) {
        this.poster = document.createElement('img');
        this.poster.setAttribute('data-film-poster', '');
        this.poster.className = 'cp-film__poster';
        this.poster.alt = '';
        this.poster.setAttribute('aria-hidden', 'true');
        this.poster.src = posterUrl;
        this.querySelector('.cp-film__stage').append(this.poster);
      }
      this.resetPoster();
      this.playButton = this.querySelector('[data-film-play]');
      this.seek = this.querySelector('[data-film-seek]');
      this.muteButton = this.querySelector('[data-film-mute]');
      this.fullButton = this.querySelector('[data-film-fullscreen]');
      this.status = this.querySelector('[data-film-status]');
      this.scrubbing = false;
      this.playPending = false;
      this.playVersion = (this.playVersion || 0) + 1;
      this.playButton.addEventListener('click', () => this.togglePlay(), options);
      this.muteButton.addEventListener('click', () => { this.video.muted = !this.video.muted; this.sync(); }, options);
      this.fullButton.addEventListener('click', () => this.fullscreen(), options);
      this.seek.addEventListener('input', () => {
        this.scrubbing = true;
        this.seekTo();
      }, options);
      this.seek.addEventListener('change', () => { this.seekTo(); this.scrubbing = false; this.sync(); }, options);
      this.seek.addEventListener('blur', () => { this.scrubbing = false; this.sync(); }, options);
      ['play','playing','pause','ended','loadedmetadata','durationchange','timeupdate','volumechange','seeked'].forEach(name => {
        this.video.addEventListener(name, () => {
          if (name === 'pause' || name === 'ended') { this.playVersion++; this.playPending = false; this.cancelPosterFrame(); this.removeAttribute('aria-busy'); if (this.status.textContent === 'Video loading…') this.status.textContent = ''; }
          if (name === 'playing') { this.status.textContent = ''; this.removeAttribute('aria-busy'); this.revealFrame(); }
          if (name === 'timeupdate' && !this.video.paused) this.revealFrame();
          this.sync();
        }, options);
      });
      ['emptied','loadstart'].forEach(name => this.video.addEventListener(name, () => this.resetPoster(), options));
      this.video.addEventListener('waiting', () => { this.status.textContent = 'Video loading…'; this.setAttribute('aria-busy', 'true'); }, options);
      this.video.addEventListener('error', () => {
        this.resetPoster();
        this.removeAttribute('aria-busy');
        this.playPending = false;
        this.status.textContent = 'The video could not load. Select Play to try again.';
        this.sync();
      }, options);
      this.video.addEventListener('webkitendfullscreen', () => { this.video.controls = false; this.removeAttribute('data-native-fullscreen'); this.sync(); }, options);
      document.addEventListener('fullscreenchange', () => this.sync(), options);
      // Avoid gallery click-to-zoom or drag handlers taking over the controls.
      this.controls.addEventListener('click', event => event.stopPropagation(), options);
      this.controls.addEventListener('pointerdown', event => event.stopPropagation(), options);
      this.fullButton.hidden = !(this.requestFullscreen || this.video.webkitEnterFullscreen);
      this.video.controls = false;
      this.video.removeAttribute('controls');
      this.controls.hidden = false;
      this.setAttribute('data-film-ready', '');
      this.sync();
    }

    disconnectedCallback() {
      this.cancelPosterFrame();
      this.playVersion++;
      this.abort?.abort();
      this.video?.pause();
      if (this.video) this.video.controls = true;
      if (this.controls) this.controls.hidden = true;
      this.removeAttribute('data-film-ready');
    }

    cancelPosterFrame() {
      this.posterEpoch = (this.posterEpoch || 0) + 1;
      if (this.frameId != null) this.video?.cancelVideoFrameCallback?.(this.frameId);
      if (this.paintId != null) cancelAnimationFrame(this.paintId);
      this.frameId = this.paintId = null;
    }

    resetPoster() {
      this.cancelPosterFrame();
      if (this.poster) this.poster.hidden = false;
    }

    revealFrame() {
      if (!this.poster || this.poster.hidden || this.frameId != null || this.paintId != null || this.video.paused) return;
      const epoch = this.posterEpoch;
      const reveal = () => {
        if (epoch !== this.posterEpoch || !this.isConnected || this.video.paused) return;
        this.poster.hidden = true;
      };
      if (this.video.requestVideoFrameCallback) {
        this.frameId = this.video.requestVideoFrameCallback(() => {
          this.frameId = null;
          if (epoch !== this.posterEpoch) return;
          this.paintId = requestAnimationFrame(() => { this.paintId = null; reveal(); });
        });
      } else if (this.video.readyState >= 2) {
        // Older browsers: allow a decoded frame two paint opportunities.
        this.paintId = requestAnimationFrame(() => {
          if (epoch !== this.posterEpoch) return;
          this.paintId = requestAnimationFrame(() => { this.paintId = null; reveal(); });
        });
      }
    }

    sync() {
      const playing = this.playPending || (!this.video.paused && !this.video.ended);
      this.playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      this.querySelector('[data-film-play-icon]').hidden = playing;
      this.querySelector('[data-film-pause-icon]').hidden = !playing;
      this.muteButton.setAttribute('aria-label', this.video.muted ? 'Unmute video' : 'Mute video');
      this.muteButton.setAttribute('aria-pressed', String(this.video.muted));
      this.querySelector('[data-film-sound-icon]').hidden = this.video.muted;
      this.querySelector('[data-film-muted-icon]').hidden = !this.video.muted;
      const duration = this.video.duration;
      this.seek.disabled = !Number.isFinite(duration) || duration <= 0;
      if (!this.scrubbing) this.seek.value = this.seek.disabled ? '0' : String(Math.round(this.video.currentTime / duration * 1000));
      this.seek.setAttribute('aria-valuetext', `${time(this.video.currentTime)} of ${time(duration)}`);
      this.querySelector('[data-film-time]').textContent = time(this.video.currentTime);
      this.fullButton.setAttribute('aria-label', document.fullscreenElement === this ? 'Exit full screen' : 'Full screen');
    }

    async togglePlay() {
      if (this.playPending || (!this.video.paused && !this.video.ended)) {
        this.playVersion++;
        this.playPending = false;
        this.video.pause();
        this.cancelPosterFrame();
        this.removeAttribute('aria-busy');
        this.status.textContent = '';
        this.sync();
        return;
      }
      const version = ++this.playVersion;
      this.playPending = true;
      this.status.textContent = 'Video loading…';
      this.setAttribute('aria-busy', 'true');
      this.sync();
      try {
        if (this.video.error) { this.resetPoster(); this.video.load(); }
        if (this.video.ended) { this.resetPoster(); this.video.currentTime = 0; }
        await this.video.play();
        if (!this.isConnected || version !== this.playVersion) return;
      } catch (error) {
        this.cancelPosterFrame();
        this.removeAttribute('aria-busy');
        if (version === this.playVersion && error.name !== 'AbortError') this.status.textContent = 'Unable to play the video. Please select Play to retry.';
      } finally {
        if (version === this.playVersion) { this.playPending = false; this.sync(); }
      }
    }

    seekTo() {
      if (Number.isFinite(this.video.duration) && this.video.duration > 0) {
        this.video.currentTime = Number(this.seek.value) / 1000 * this.video.duration;
        this.querySelector('[data-film-time]').textContent = time(this.video.currentTime);
        this.seek.setAttribute('aria-valuetext', `${time(this.video.currentTime)} of ${time(this.video.duration)}`);
      }
    }

    async fullscreen() {
      try {
        if (document.fullscreenElement === this) await document.exitFullscreen();
        else if (this.requestFullscreen) await this.requestFullscreen();
        else if (this.video.webkitEnterFullscreen) { this.setAttribute('data-native-fullscreen', ''); this.video.controls = true; this.video.webkitEnterFullscreen(); }
      } catch (_) {
        this.removeAttribute('data-native-fullscreen');
        this.video.controls = false;
        this.status.textContent = 'Full screen is not available. You can keep watching here.';
      }
      this.sync();
    }
  }
  customElements.define('cp-film-player', FilmPlayer);
})();
